import React, { useEffect, useState } from "react";
import { Download, Share, MoreVertical, Smartphone, Monitor, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { getInstallEvent, onInstallEvent, promptInstall, isIOS, isAndroid, isStandalone } from "../lib/installPrompt";

function Steps({ title, icon: Icon, steps }) {
  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-[#fbf6ef] p-3">
      <div className="flex items-center gap-2 font-bold text-sm text-[#2b1410]"><Icon className="h-4 w-4" /> {title}</div>
      <ol className="mt-1.5 space-y-1 text-sm text-[#5a3d33] list-decimal pl-5">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </div>
  );
}

export function InstallHomeButton({ appName = "questa app", className = "", compact = false }) {
  const [ev, setEv] = useState(getInstallEvent());
  const [open, setOpen] = useState(false);
  const [menuInstalled, setMenuInstalled] = useState(false);
  useEffect(() => onInstallEvent(setEv), []);
  useEffect(() => {
    if (!open || !navigator.getInstalledRelatedApps) return;
    navigator.getInstalledRelatedApps().then((apps) => setMenuInstalled(apps.length > 0)).catch(() => {});
  }, [open]);
  if (isStandalone()) return null;

  const click = async () => {
    if (ev) { const ok = await promptInstall(); if (ok) return; }
    setOpen(true);
  };
  const ios = isIOS(), android = isAndroid();
  const inAppHint = <>Se hai aperto questa pagina <b>dall'app Admin</b>: tocca <MoreVertical className="inline h-3.5 w-3.5" /> in alto a destra → <b>Apri in Chrome</b>, poi ripeti i passaggi.</>;

  return (
    <>
      <button type="button" onClick={click} data-testid="install-home-btn" title="Aggiungi alla Home"
        className={className || (compact
          ? "h-9 w-9 rounded-full bg-white/10 inline-flex items-center justify-center hover:bg-white/20"
          : "inline-flex items-center gap-2 rounded-full border border-[#4a1c17]/25 bg-white px-4 py-2 text-sm font-semibold text-[#4a1c17] hover:bg-[#fbf6ef]")}>
        <Download className="h-4 w-4" />{!compact && <span>Aggiungi alla Home</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#f7ebdf] text-[#2b1410]" data-testid="install-home-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Aggiungi {appName} alla Home</DialogTitle>
            <DialogDescription>Nessun download: l'icona apre direttamente questa pagina a tutto schermo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <div className={`rounded-2xl border p-3 text-sm ${menuInstalled ? "border-amber-500 bg-amber-50 text-[#5a3d33]" : "border-[#e8dcc8] bg-[#fbf6ef] text-[#5a3d33]"}`} data-testid="install-home-menu-conflict">
              <div className="flex items-center gap-2 font-bold text-[#2b1410]"><AlertTriangle className="h-4 w-4 text-amber-600" /> {menuInstalled ? "Su questo dispositivo è installata l'app Menu" : "Vedi \"Apri nell'app\" invece di Installa?"}</div>
              <p className="mt-1">Chrome non installa {appName} finché c'è l'app <b>Menu</b> (copre tutto il sito). Soluzione in 3 mosse:</p>
              <ol className="mt-1 list-decimal pl-5 space-y-0.5">
                <li>{ios ? <>Tieni premuta l'icona <b>Menu</b> sulla Home → <b>Elimina app</b></> : android ? <>Tieni premuta l'icona <b>Menu</b> sulla Home → <b>Disinstalla</b></> : <>Apri <b>chrome://apps</b> → tasto destro su <b>Il Pozzo dei Giganti — Menu</b> → <b>Rimuovi da Chrome</b></>}</li>
                <li>Torna qui e installa <b>{appName}</b> con i passaggi sotto</li>
                <li>Se ti serve, reinstalla il Menu dalla pagina iniziale: le due app convivono</li>
              </ol>
            </div>
            {(android || (!ios && !android)) && (
              <Steps title="Android · Chrome" icon={Smartphone} steps={[
                <>Tocca il menu <MoreVertical className="inline h-3.5 w-3.5" /> in alto a destra</>,
                <>Scegli <b>Installa app</b> oppure <b>Aggiungi a schermata Home</b></>,
                <>Conferma: l'icona compare sulla Home</>,
              ]} />
            )}
            {(ios || (!ios && !android)) && (
              <Steps title="iPhone · Safari" icon={Share} steps={[
                <>Tocca <b>Condividi</b> <Share className="inline h-3.5 w-3.5" /> in basso al centro</>,
                <>Scorri e scegli <b>Aggiungi alla schermata Home</b></>,
                <>Tocca <b>Aggiungi</b> in alto a destra</>,
              ]} />
            )}
            {!ios && !android && (
              <Steps title="Mac / PC · Chrome" icon={Monitor} steps={[
                <>Clicca l'icona <b>Installa</b> a destra nella barra degli indirizzi</>,
                <>Oppure menu <MoreVertical className="inline h-3.5 w-3.5" /> → <b>Trasmetti, salva e condividi</b> → <b>Installa pagina come app…</b></>,
                <>L'app finisce nel Launchpad / Dock</>,
              ]} />
            )}
            <p className="text-xs text-[#7c5a4e]" data-testid="install-home-inapp-hint">{inAppHint}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
